/** ***********
*
* <workers-functions.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import moment from 'moment';
import { RoleLevelEnum, UserRoleLevel } from '../models/user-right';
import { AdminAssetsWorkerInput, EsriMapWorkerInput, FamilyUsersRoleLevelWorkerInput, FormatAssetsWorkerInput } from './models';

/**
 * Contains the static functions to be used in the worker or as fallback when the Worker API is not available
 */
export class WorkerFunction {

  /**
   * Format assets for the map
   */
  public static formatAssets({ data }: FormatAssetsWorkerInput) {
    const { assets } = data;
    for (const asset of assets) {
      if (asset.last_event && asset.last_event.lng && asset.last_event.lat && asset.tracker && Object.keys(asset.tracker).length) {
        asset.last_event.source = asset.last_event.source.toLowerCase().replace('geobeacon', 'beacon');
        asset.last_event.state = asset.last_event.state?.toLowerCase().replace('on_the_move', 'otm');
        if (asset.last_event.event_time && !moment(asset.last_event.event_time, 'L LT', true).isValid()) {
          asset.last_event.event_time = moment(asset.last_event.event_time).format('L LT');
          asset.lastEventTimestamp = moment(asset.last_event.event_time, 'L LT').unix() || 0;
        }
        if (asset.last_no_gps && !moment(asset.last_no_gps.event_time, 'L LT', true).isValid()) {
          asset.last_no_gps.event_time = moment(asset.last_no_gps.event_time).format('L LT');
        }
        if (asset.tracker.keepAlive && !moment(asset.tracker.keepAlive, 'L LT', true).isValid()) {
          asset.tracker.keepAlive = moment(asset.tracker.keepAlive).format('L LT');
          asset.keepAliveTimestamp = moment(asset.tracker.keepAlive, 'L LT').unix() || 0;
        }
      }
    }
    const output = { assets };
    return output;
  }

  /**
   * Format assets for admin view
   */
  public static adminAssets({ data }: AdminAssetsWorkerInput) {
    const { assets, authorizedFamiliesIds } = data;

    const assetsArray = Object.values(assets).map((asset) => {
      asset.authorize = authorizedFamiliesIds.includes(asset.familyId);
      return asset;
    });
    const output = { assets: assetsArray };
    return output;
  }

  /**
   * Format assets for esri map
   */
  public static esriMap(message: EsriMapWorkerInput) {
    const assets = message.data;
    const assetArray = Object.values(assets);
    return assetArray;
  }

  /**
   * Format the role levels of the users for the Manage Users of Family table
   */
  public static familyUsersRoleLevel({ data }: FamilyUsersRoleLevelWorkerInput) {
    const { allUsers, familyUsers } = data;
    return allUsers.map(
      (user): UserRoleLevel => {
        const familyUser = familyUsers.find(u => u.userId === user.userId);
        return {
          data: familyUser?.userId ?? user.userId,
          email: familyUser?.mail ?? user.email,
          famRightLevel: familyUser?.famRightLevel ?? RoleLevelEnum.basic,
          userId: user.id,
          fromGroupId: familyUser?.fromGroupId,
        };
      }
    );
  }

}
